import { useState } from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type PaginationState,
  type SortingState,
} from '@tanstack/react-table';
import type { Role } from '@flintmint/core-shared/constants';
import { Skeleton } from '@flintmint/web-shared/components/skeleton';

import { appRoutes } from '@/constants/routes';
import { formatUsd } from '@/utils/format-values';
import { PlanCell } from './plan-cell';
import { RolesCell } from './roles-cell';
import type { UserRow } from './use-users';
import styles from './users-table.module.scss';

type UsageState = 'loading' | 'failed' | 'loaded';

interface TableMeta {
  rolesByUserId: Map<string, Role[]>;
  rolesLoading: boolean;
  usage: UsageState;
}

const col = createColumnHelper<UserRow>();

const AVG_COST_COLUMN_ID = 'avgMonthlyCostUsd';

function getName(row: UserRow): string {
  if (row.displayName) return row.displayName;
  const parts = [row.firstName, row.lastName].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : '–';
}

function formatDate(val?: string): string {
  if (!val) return '–';
  return new Date(val).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatMonth(month: string | null): string {
  if (!month) return '–';
  const [year, monthNumber] = month.split('-').map(Number);
  return new Date(year, monthNumber - 1, 1).toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
}

const columns = [
  col.accessor('email', {
    header: 'Email',
    cell: (i) => (
      <Link className={styles.userLink} to={buildUserDetailsPath(i.row.original.userId)}>
        {i.getValue()}
      </Link>
    ),
  }),
  col.accessor((row) => getName(row), { id: 'name', header: 'Name' }),
  col.accessor('createdAt', { header: 'Joined', cell: (i) => formatDate(i.getValue()) }),
  col.display({ id: 'plan', header: 'Plan', cell: (i) => <PlanCell row={i.row.original} /> }),
  col.accessor('lastUsageMonth', {
    header: 'Last Usage',
    cell: (i) => {
      const { usage } = i.table.options.meta as TableMeta;
      if (usage === 'loading') return <Skeleton width={80} height={16} />;
      return usage === 'failed' ? 'Unavailable' : formatMonth(i.getValue());
    },
  }),
  col.accessor((row) => row.avgMonthlyCostUsd ?? -1, {
    id: AVG_COST_COLUMN_ID,
    header: 'Avg Cost / Month',
    cell: (i) => {
      const row = i.row.original;
      const { usage } = i.table.options.meta as TableMeta;
      if (usage === 'loading') return <Skeleton width={80} height={16} />;
      if (usage === 'failed') return 'Unavailable';
      if (row.hasPricingGap) return <span className={styles.warning}>Pricing gap</span>;
      return row.activeMonthCount > 0 ? (
        formatUsd(row.avgMonthlyCostUsd, { nullFallback: '–' })
      ) : (
        <span className={styles.zero}>–</span>
      );
    },
  }),
  col.display({
    id: 'roles',
    header: 'Roles',
    cell: (i) => {
      const row = i.row.original;
      const { rolesByUserId, rolesLoading } = i.table.options.meta as TableMeta;
      if (rolesLoading) return <Skeleton width={120} height={16} />;
      const roles = rolesByUserId.get(row.userId) ?? [];
      return <RolesCell user={row} roles={roles} />;
    },
  }),
];

interface Props {
  rows: UserRow[];
  rolesByUserId: Map<string, Role[]>;
  rolesLoading: boolean;
  usage: UsageState;
}

const PAGE_SIZE = 1000;

export function UsersTable({ rows, rolesByUserId, rolesLoading, usage }: Props) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'avgMonthlyCostUsd', desc: true }]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: PAGE_SIZE });

  const table = useReactTable({
    data: rows,
    columns,
    getRowId: (row) => row.userId,
    state: { sorting, pagination },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    meta: { rolesByUserId, rolesLoading, usage } satisfies TableMeta,
  });

  if (rows.length === 0) {
    return <p className={styles.empty}>No users found.</p>;
  }

  const { pageIndex, pageSize } = table.getState().pagination;
  const totalRows = table.getFilteredRowModel().rows.length;
  const start = pageIndex * pageSize + 1;
  const end = Math.min(start + pageSize - 1, totalRows);

  return (
    <>
      <table className={styles.table}>
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => {
                const sorted = header.column.getIsSorted();
                return (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className={clsx({
                      [styles.sortable]: header.column.getCanSort(),
                      [styles.numericCell]: header.column.id === AVG_COST_COLUMN_ID,
                    })}
                    aria-sort={
                      !header.column.getCanSort()
                        ? undefined
                        : sorted === 'asc'
                        ? 'ascending'
                        : sorted === 'desc'
                        ? 'descending'
                        : 'none'
                    }
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {sorted === 'asc' ? ' ▲' : sorted === 'desc' ? ' ▼' : ''}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className={usage === 'loaded' && row.original.activeMonthCount === 0 ? styles.inactive : undefined}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className={clsx({ [styles.numericCell]: cell.column.id === AVG_COST_COLUMN_ID })}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {totalRows > pageSize && (
        <div className={styles.pagination}>
          <button onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
            ← Prev
          </button>
          <span>
            {start}–{end} of {totalRows}
          </span>
          <button onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
            Next →
          </button>
        </div>
      )}
    </>
  );
}

const SKELETON_ROW_COUNT = 6;
const SKELETON_CELL_WIDTHS = [180, 120, 90, 140, 80, 80, 120];

export function UsersTableSkeleton() {
  return (
    <table className={styles.table}>
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column.header as string} className={clsx({ [styles.numericCell]: column.id === AVG_COST_COLUMN_ID })}>
              {column.header as string}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: SKELETON_ROW_COUNT }, (_, rowIndex) => (
          <tr key={rowIndex} data-testid="user-row-skeleton">
            {SKELETON_CELL_WIDTHS.map((width, cellIndex) => (
              <td key={cellIndex}>
                <Skeleton width={width} height={16} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function buildUserDetailsPath(userId: string): string {
  return `${appRoutes.users}/${encodeURIComponent(userId)}`;
}
